import {
    BonusSettlementCustomerStatus,
    BonusSettlementSupplierStatus,
    DemandState,
    DocumentStatus,
    ImportSupplierStatus,
    InquiryOfferState,
    SalesOrderStatus,
} from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactNode } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';

import { PoolMultiSelect, PoolSelect } from '../../select';
import { useFilter } from '../context';

export interface StatusFilterProps {
    documentStatus?:
        | DocumentStatus[]
        | DemandState[]
        | InquiryOfferState[]
        | SalesOrderStatus[]
        | BonusSettlementSupplierStatus[]
        | BonusSettlementCustomerStatus[]
        | ImportSupplierStatus[];
    /** Several statuses at once, e.g. "Erfasst + Läuft" (GF 30.10.2026, #2340). */
    multiple?: boolean;
    /**
     * The message id of a status, where a kind of document names one of its
     * own: an order whose invoice was made from it is "Abgerechnet" (#2264).
     */
    translationIdOf?: (status: string) => string;
}

export const StatusFilter = (props: StatusFilterProps) => {
    const { filter, updateFilter, registerFilterMap } = useFilter();
    const intl = useIntl();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const translationIdOf =
        props.translationIdOf ?? ((status: string) => getDocumentStatusTranslationId(status as any));

    const onSelected = (
        status?:
            | DocumentStatus
            | DemandState
            | InquiryOfferState
            | SalesOrderStatus
            | BonusSettlementSupplierStatus
            | BonusSettlementCustomerStatus
            | ImportSupplierStatus
    ) => {
        filter.delete('status');
        if (status) {
            filter.set('status', status);
        }
        updateFilter(filter);
    };

    const getDisplayValue = (statusValue: unknown): ReactNode => {
        if (typeof statusValue !== 'string') {
            return '';
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const status = statusValue as
            | DocumentStatus
            | DemandState
            | InquiryOfferState
            | BonusSettlementSupplierStatus
            | BonusSettlementCustomerStatus
            | ImportSupplierStatus;
        return <FormattedMessage id={translationIdOf(status)} />;
    };

    registerFilterMap('status', getDisplayValue);

    const selectableStatus = props.documentStatus ?? Object.values(DocumentStatus);

    if (props.multiple) {
        const onPicked = (statuses: string[]): void => {
            filter.delete('status');
            statuses.forEach(status => filter.append('status', status));
            updateFilter(filter);
        };

        return (
            <PoolMultiSelect
                displayEmpty={true}
                name="status"
                label={<FormattedMessage id="app.filter.statusLabel" />}
                fullWidth={true}
                value={filter.getAll('status')}
                onValueChange={onPicked}
                noSorting={true}
                options={selectableStatus.map(status => ({
                    value: status,
                    displayValue: intl.formatMessage({ id: translationIdOf(status) }),
                }))}
            />
        );
    }

    return (
        <PoolSelect
            displayEmpty={true}
            name="status"
            label={<FormattedMessage id="app.filter.statusLabel" />}
            fullWidth={true}
            value={filter.get('status')}
            onValueChange={onSelected}
            noSorting={true}
            options={selectableStatus.map(status => {
                return {
                    value: status,
                    displayValue: <FormattedMessage id={translationIdOf(status)} />,
                };
            })}
        />
    );
};
